import {
  OkResponse,
  Printer,
  PrinterList,
  PrinterOverview,
  PrinterSlotList,
  formatBRL,
  formatDate,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { AffixInput, Field } from '../../components/Form';
import { Modal, Panel } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import {
  formatG,
  useInvalidateInventory,
  usePrinterSlots,
  useSpools,
} from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import { parseBrDate, parseCentavos } from '../../lib/numbers';
import { useIsAdmin } from '../../lib/session';
import { Swatch } from '../inventory/spool-common';

const h1 = (h: number) => h.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

export const useOverview = (printerId: string) =>
  useQuery({
    queryKey: ['printers', printerId, 'overview'],
    queryFn: () => api.get(`/printers/${printerId}/overview`, PrinterOverview),
  });

function useRefreshPrinter() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['printers'] });
}

/** Stat cards (board Impressora-Detalhe): hour meter, use, failure rate, cost per hour. */
export function StatCards({ printer }: { printer: Printer }) {
  const o = useOverview(printer.id).data;
  return (
    <div className="grid s">
      <div className="card">
        <p className="xs muted">Horímetro</p>
        <p className="big" style={{ fontSize: 30 }}>
          {h1(printer.hourMeterHours)} h
        </p>
        <p className="xs muted">de {printer.lifetimeHours.toLocaleString('pt-BR')} h esperadas</p>
      </div>
      <div className="card">
        <p className="xs muted">Uso nos últimos 30 dias</p>
        <p className="big" style={{ fontSize: 30 }}>
          {o ? `${h1(o.utilization30dPct)}%` : '—'}
        </p>
        <p className="xs muted">{o ? `${h1(o.hours30d)} h de 720 h` : ' '}</p>
      </div>
      <div className="card">
        <p className="xs muted">Taxa de falha real</p>
        <p className="big" style={{ fontSize: 30 }}>
          {o?.failureRatePct != null ? `${h1(o.failureRatePct)}%` : '—'}
        </p>
        <p className="xs muted">
          {o ? `${o.jobsFailed} de ${o.jobsDone + o.jobsFailed} impressões` : ' '}
        </p>
      </div>
    </div>
  );
}

/** "Usar este valor": logged parts ÷ print hours as the maintenance per hour (Admin). */
export function SuggestedMaintenance({ printer }: { printer: Printer }) {
  const isAdmin = useIsAdmin();
  const o = useOverview(printer.id).data;
  const toast = useToast();
  const refresh = useRefreshPrinter();
  const apply = useMutation({
    mutationFn: (v: number) =>
      api.patch(`/printers/${printer.id}`, Printer, { maintenancePerHourCentavos: v }),
    onSuccess: (p) => {
      refresh();
      toast(
        `Manutenção por hora: ${formatBRL(p.maintenancePerHourCentavos)}. Orçamentos novos usam o novo valor.`,
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  if (!o) return null;
  const s = o.suggestedMaintenancePerHourCentavos;
  return (
    <div className="card sunk col" style={{ gap: 6, padding: 12, borderRadius: 12 }}>
      <span className="sm">
        Peças registradas ÷ horas: {formatBRL(o.maintenanceCostCentavos)} ÷ {h1(o.hourMeterHours)} h
        {s !== null ? (
          <>
            {' '}
            = <b>{formatBRL(s)}/h</b>
          </>
        ) : null}
      </span>
      <span className="xs muted">
        Hoje os orçamentos usam {formatBRL(printer.maintenancePerHourCentavos)}/h.
        {s === null
          ? ' A sugestão aparece quando houver horas impressas e manutenção com custo.'
          : ''}
      </span>
      {isAdmin && s !== null && s !== printer.maintenancePerHourCentavos ? (
        <button
          type="button"
          className="btn sm"
          style={{ alignSelf: 'flex-start' }}
          disabled={apply.isPending}
          onClick={() => apply.mutate(s)}
        >
          Usar este valor
        </button>
      ) : null}
    </div>
  );
}

export function LogDialog({
  printer,
  taskId,
  onClose,
}: {
  printer: Printer;
  taskId: string | null;
  onClose: () => void;
}) {
  const o = useOverview(printer.id).data;
  const toast = useToast();
  const refresh = useRefreshPrinter();
  const [task, setTask] = useState<string | null>(taskId);
  const [other, setOther] = useState('');
  const [date, setDate] = useState(formatIsoDate(isoDateInSaoPaulo()));
  const [cost, setCost] = useState('0,00');
  const [parts, setParts] = useState('');
  const [tried, setTried] = useState(false);
  const iso = parseBrDate(date);
  const chosen = o?.tasks.find((t) => t.id === task);
  const valid =
    iso !== null && (task !== null || other.trim().length > 0) && parseCentavos(cost) !== null;
  const save = useMutation({
    mutationFn: () =>
      api.post(`/printers/${printer.id}/maintenance-log`, PrinterOverview, {
        taskId: task,
        description: task ? null : other.trim(),
        doneOn: iso,
        parts: parts.trim() || null,
        costCentavos: parseCentavos(cost) ?? 0,
      }),
    onSuccess: () => {
      refresh();
      toast(
        chosen
          ? `${chosen.name} feita. Próxima em ${chosen.intervalHours} h.`
          : 'Manutenção registrada.',
      );
      onClose();
    },
  });
  return (
    <Panel label={taskId ? 'Marcar como feita' : 'Registrar manutenção'} onClose={onClose}>
      <div className="row wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Tarefa">
        {(o?.tasks ?? []).map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={task === t.id}
            className={task === t.id ? 'chip on' : 'chip'}
            onClick={() => setTask(t.id)}
          >
            {t.name}
          </button>
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={task === null}
          className={task === null ? 'chip on' : 'chip'}
          onClick={() => setTask(null)}
        >
          Outra
        </button>
      </div>
      {task === null ? (
        <Field
          id="ml-desc"
          label="Descrição"
          error={tried && !other.trim() ? 'Escolha a tarefa ou descreva a manutenção.' : null}
        >
          <input
            id="ml-desc"
            className="input"
            placeholder="Ex.: trocar ventoinha da peça"
            value={other}
            onChange={(e) => setOther(e.target.value)}
          />
        </Field>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field id="ml-date" label="Data" error={tried && !iso ? 'Use DD/MM/AAAA.' : null}>
          <input
            id="ml-date"
            className="input"
            inputMode="numeric"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field id="ml-cost" label="Custo">
          <AffixInput id="ml-cost" prefix="R$" value={cost} onChange={setCost} />
        </Field>
      </div>
      <Field id="ml-parts" label="Peças usadas">
        <input
          id="ml-parts"
          className="input"
          placeholder="Ex.: graxa, bico 0,4 mm"
          value={parts}
          onChange={(e) => setParts(e.target.value)}
        />
      </Field>
      {o ? (
        <div className="card sunk col" style={{ gap: 2, padding: 12, borderRadius: 12 }}>
          <div className="kv sm">
            <span className="muted">Horímetro registrado</span>
            <span className="v">{h1(o.hourMeterHours)} h</span>
          </div>
          {chosen ? (
            <div className="kv sm">
              <span className="muted">Próxima vez</span>
              <span className="v">
                em {chosen.intervalHours} h ({h1(o.hourMeterHours + chosen.intervalHours)} h)
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <button
        type="button"
        className="btn p block"
        disabled={save.isPending}
        onClick={() => {
          setTried(true);
          if (valid) save.mutate();
        }}
      >
        {taskId ? 'Marcar como feita' : 'Registrar'}
      </button>
    </Panel>
  );
}

function TaskDialog({ printer, onClose }: { printer: Printer; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshPrinter();
  const [name, setName] = useState('');
  const [every, setEvery] = useState('300');
  const n = /^\d+$/.test(every.trim()) ? Number(every) : null;
  const save = useMutation({
    mutationFn: () =>
      api.post(`/printers/${printer.id}/maintenance-tasks`, PrinterOverview, {
        name: name.trim(),
        intervalHours: n,
      }),
    onSuccess: () => {
      refresh();
      toast(`Tarefa “${name.trim()}” adicionada.`);
      onClose();
    },
  });
  return (
    <Modal
      title="Nova tarefa"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={!name.trim() || !n || save.isPending}
            onClick={() => save.mutate()}
          >
            Adicionar tarefa
          </button>
        </>
      }
    >
      <Field id="mt-name" label="Tarefa">
        <input
          id="mt-name"
          className="input"
          placeholder="Ex.: limpar ventoinha da câmara"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field
        id="mt-every"
        label="Repetir a cada"
        help={
          n
            ? `Começa a contar agora (${h1(printer.hourMeterHours)} h). Primeira vez: em ${n} h (${h1(printer.hourMeterHours + n)} h).`
            : undefined
        }
      >
        <AffixInput
          id="mt-every"
          unit="horas de impressão"
          value={every}
          onChange={setEvery}
          inputMode="numeric"
        />
      </Field>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}

/** "Plano de manutenção" and "Registro de manutenção". */
export function MaintenanceCard({ printer }: { printer: Printer }) {
  const isAdmin = useIsAdmin();
  const o = useOverview(printer.id).data;
  const [dialog, setDialog] = useState<
    { kind: 'log'; taskId: string | null } | { kind: 'task' } | null
  >(null);
  const toast = useToast();
  const refresh = useRefreshPrinter();
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/printers/${printer.id}/maintenance-tasks/${id}`, OkResponse),
    onSuccess: refresh,
    onError: (err) => toast(errorMessage(err)),
  });
  if (!o) return null;
  const cols = '1.6fr 80px 90px 1fr 120px';
  return (
    <>
      <section className="card flat">
        <div className="row between" style={{ padding: '16px 18px 6px' }}>
          <h2>Plano de manutenção</h2>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => setDialog({ kind: 'log', taskId: null })}
            >
              Registrar manutenção
            </button>
            {isAdmin ? (
              <button type="button" className="btn sm" onClick={() => setDialog({ kind: 'task' })}>
                + Tarefa
              </button>
            ) : null}
          </div>
        </div>
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 560 }}>
            <span>Tarefa</span>
            <span className="num">A cada</span>
            <span className="num">Última</span>
            <span>Situação</span>
            <span />
          </div>
          {o.tasks.map((t) => (
            <div
              key={t.id}
              className={t.state === 'due' ? 'gtr late' : 'gtr'}
              style={{ gridTemplateColumns: cols, minWidth: 560 }}
            >
              <span>{t.name}</span>
              <span className="num">{t.intervalHours} h</span>
              <span className="num">
                {t.lastDoneHours !== null ? `${h1(t.lastDoneHours)} h` : '—'}
              </span>
              <span>
                <span
                  className={
                    t.state === 'due' ? 'badge err' : t.state === 'soon' ? 'badge warn' : 'badge ok'
                  }
                >
                  {t.dueInHours < 0
                    ? `Atrasada ${h1(-t.dueInHours)} h`
                    : t.dueInHours === 0
                      ? 'Vence agora'
                      : `Em ${h1(t.dueInHours)} h`}
                </span>
              </span>
              <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className={t.state === 'due' ? 'btn sm p' : 'btn sm'}
                  onClick={() => setDialog({ kind: 'log', taskId: t.id })}
                >
                  Feito
                </button>
                {isAdmin ? (
                  <button
                    type="button"
                    className="btn sm g icon"
                    aria-label={`Remover ${t.name}`}
                    onClick={() => remove.mutate(t.id)}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            </div>
          ))}
          {o.tasks.length === 0 ? (
            <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
              <span className="sm muted">
                Nenhuma tarefa ainda. Ex.: lubrificar eixos a cada 200 h, limpar o bico a cada 100
                h.
              </span>
            </div>
          ) : null}
        </div>
      </section>
      <section className="card flat">
        <div className="row between" style={{ padding: '16px 18px 6px' }}>
          <h2>Registro de manutenção</h2>
          <span className="xs muted">
            {o.log.length} {o.log.length === 1 ? 'registro' : 'registros'}
          </span>
        </div>
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div
            className="gtr head"
            style={{ gridTemplateColumns: '100px 1.4fr 1.4fr 90px 90px', minWidth: 560 }}
          >
            <span>Data</span>
            <span>Tarefa</span>
            <span>Peças</span>
            <span className="num">Horímetro</span>
            <span className="num">Custo</span>
          </div>
          {o.log.map((l) => (
            <div
              key={l.id}
              className="gtr"
              style={{ gridTemplateColumns: '100px 1.4fr 1.4fr 90px 90px', minWidth: 560 }}
            >
              <span className="tab sm">{formatIsoDate(l.doneOn)}</span>
              <span>{l.description}</span>
              <span className="sm muted">{l.parts ?? '—'}</span>
              <span className="num">{h1(l.hourMeterHours)} h</span>
              <span className="num">{formatBRL(l.costCentavos)}</span>
            </div>
          ))}
          {o.log.length === 0 ? (
            <div className="gtr" style={{ gridTemplateColumns: '1fr' }}>
              <span className="sm muted">Nada registrado ainda.</span>
            </div>
          ) : null}
        </div>
      </section>
      {dialog?.kind === 'log' ? (
        <LogDialog printer={printer} taskId={dialog.taskId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'task' ? (
        <TaskDialog printer={printer} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}

/** "Horas por material": stacked bar with the legend. */
export function MaterialsCard({ printer }: { printer: Printer }) {
  const o = useOverview(printer.id).data;
  if (!o || o.hoursByMaterial.length === 0) return null;
  const total = o.hoursByMaterial.reduce((n, m) => n + m.hours, 0) || 1;
  const colors = ['var(--pe)', 'var(--co)', '#C9A227', '#7FB2BA', 'var(--ink2)'];
  return (
    <section className="card col" style={{ gap: 10 }}>
      <h2 style={{ fontSize: 18 }}>Horas por material</h2>
      <div className="row" style={{ height: 14, borderRadius: 7, overflow: 'hidden' }}>
        {o.hoursByMaterial.map((m, i) => (
          <span
            key={m.material}
            style={{
              display: 'block',
              height: '100%',
              width: `${(m.hours / total) * 100}%`,
              background: colors[i % colors.length],
            }}
          />
        ))}
      </div>
      <div className="row wrap" style={{ gap: 12 }}>
        {o.hoursByMaterial.map((m, i) => (
          <span key={m.material} className="row sm" style={{ gap: 6 }}>
            <span className="dot" style={{ background: colors[i % colors.length] }} />
            {m.material} {h1(m.hours)} h
          </span>
        ))}
      </div>
    </section>
  );
}

/** "Carretéis carregados": AMS slots and the external holder. */
export function SlotsCard({ printer }: { printer: Printer }) {
  const slots = usePrinterSlots(printer.id);
  const spools = useSpools({ active: true });
  const invalidate = useInvalidateInventory();
  const toast = useToast();
  const [picking, setPicking] = useState<string | null>(null);
  const load = useMutation({
    mutationFn: (v: { slot: string; spoolId: string | null }) =>
      api.post(`/printers/${printer.id}/slots/${v.slot}/load`, PrinterSlotList, {
        spoolId: v.spoolId,
      }),
    onSuccess: (_r, v) => {
      invalidate();
      const code = spools.data?.data.find((s) => s.id === v.spoolId)?.code;
      toast(
        v.spoolId
          ? `${code} carregado no ${v.slot === 'ext' ? 'externo' : `slot ${v.slot}`}.`
          : 'Slot esvaziado.',
      );
      setPicking(null);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const nozzle = useMutation({
    mutationFn: (v: { slot: string; nozzle: 'main' | 'aux' }) =>
      api.patch(`/printers/${printer.id}/slots/${v.slot}`, PrinterSlotList, { nozzle: v.nozzle }),
    onSuccess: () => invalidate(),
    onError: (err) => toast(errorMessage(err)),
  });
  const list = slots.data?.data ?? [];
  return (
    <section className="card col" style={{ gap: 4 }}>
      <div className="row between">
        <h2 style={{ fontSize: 18 }}>Carretéis carregados</h2>
        <span className="xs muted">{printer.multicolorUnit ?? 'sem AMS'} · toque para trocar</span>
      </div>
      {list.map((s) => (
        <div
          key={s.slot}
          className="row"
          style={{ gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line)' }}
        >
          <span className="tab" style={{ width: 34, fontWeight: 600 }}>
            {s.slot === 'ext' ? 'Ext.' : s.slot}
          </span>
          <Swatch hex={s.spool?.filament.colorHex ?? null} size={16} />
          <span className="grow" style={{ minWidth: 0 }}>
            {s.spool ? (
              <Link
                to={`/carreteis/${s.spool.id}`}
                className="trunc sm"
                style={{ display: 'block' }}
              >
                {s.spool.filament.name} · {s.spool.code}
              </Link>
            ) : (
              <span className="sm muted">Vazio · sem carretel</span>
            )}
            <select
              className="input"
              aria-label={`Bico do ${s.label}`}
              style={{ height: 28, width: 'auto', fontSize: 12, marginTop: 2 }}
              value={s.nozzle}
              onChange={(e) =>
                nozzle.mutate({ slot: s.slot, nozzle: e.target.value === 'aux' ? 'aux' : 'main' })
              }
            >
              <option value="main">bico principal</option>
              <option value="aux">bico auxiliar</option>
            </select>
          </span>
          {s.spool ? (
            <span
              className={s.spool.low ? 'badge warn' : 'sm tab'}
              style={{ whiteSpace: 'nowrap' }}
            >
              {formatG(s.spool.remainingGrams)}
            </span>
          ) : null}
          <button type="button" className="btn sm" onClick={() => setPicking(s.slot)}>
            {s.spool ? 'Trocar' : 'Carregar'}
          </button>
        </div>
      ))}
      {picking ? (
        <Panel
          label={picking === 'ext' ? 'Carregar no externo' : `Carregar no slot ${picking}`}
          onClose={() => setPicking(null)}
        >
          <p className="sm muted">
            Carretéis com filamento. Se o escolhido estiver em outro slot, os dois trocam de lugar.
          </p>
          {(spools.data?.data ?? []).map((sp) => (
            <button
              key={sp.id}
              type="button"
              className="listrow"
              style={{ width: '100%' }}
              disabled={load.isPending}
              onClick={() => load.mutate({ slot: picking, spoolId: sp.id })}
            >
              <Swatch hex={sp.filament.colorHex} />
              <span className="grow sm" style={{ textAlign: 'left' }}>
                {sp.code} · {sp.filament.name}
              </span>
              <span className="xs muted">
                {formatG(sp.remainingGrams)} ·{' '}
                {sp.location
                  ? sp.location.slot === picking
                    ? 'neste slot'
                    : `no slot ${sp.location.slot}`
                  : sp.status === 'sealed'
                    ? 'lacrado'
                    : 'prateleira'}
              </span>
            </button>
          ))}
          <button
            type="button"
            className="btn block"
            onClick={() => load.mutate({ slot: picking, spoolId: null })}
          >
            Deixar o slot vazio
          </button>
          <Link to="/filamentos/carreteis" className="xs">
            Ver todos os carretéis
          </Link>
        </Panel>
      ) : null}
    </section>
  );
}

/** "Aposentar" (Admin) and the retired banner with "Desfazer". */
export function RetireDialog({ printer, onClose }: { printer: Printer; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshPrinter();
  const [counts, setCounts] = useState(true);
  const used = Math.round(printer.hourMeterHours * printer.costPerHour.low.depreciation);
  const retire = useMutation({
    mutationFn: () =>
      api.post(`/printers/${printer.id}/retire`, OkResponse, { countsForLifetime: counts }),
    onSuccess: () => {
      refresh();
      toast(`${printer.name} aposentada.`);
      onClose();
    },
  });
  return (
    <Modal
      title={`Aposentar a ${printer.name}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn d"
            disabled={retire.isPending}
            onClick={() => retire.mutate()}
          >
            Aposentar
          </button>
        </>
      }
    >
      <p className="sm">
        Ela sai da fila e do orçamento rápido. O histórico de impressões, manutenções e custos
        continua guardado.
      </p>
      <div className="card sunk col" style={{ gap: 2, padding: 12, borderRadius: 12 }}>
        <div className="kv sm">
          <span className="muted">Horímetro final</span>
          <span className="v">{h1(printer.hourMeterHours)} h</span>
        </div>
        <div className="kv sm">
          <span className="muted">Depreciação já contada</span>
          <span className="v">{formatBRL(used)}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Valor não depreciado</span>
          <span className="v">{formatBRL(Math.max(0, printer.purchasePriceCentavos - used))}</span>
        </div>
      </div>
      <button
        type="button"
        className="check sm"
        role="switch"
        aria-checked={counts}
        onClick={() => setCounts(!counts)}
      >
        <span className={counts ? 'toggle on' : 'toggle'} aria-hidden="true" />
        Usar as {h1(printer.hourMeterHours)} h finais na vida útil padrão das próximas impressoras
      </button>
      <p className="xs muted">
        A vida útil padrão é a média das impressoras aposentadas. Desligue se ela saiu por troca ou
        venda, e não por desgaste.
      </p>
      {retire.isError ? <Alert tone="err">{errorMessage(retire.error)}</Alert> : null}
    </Modal>
  );
}

/** Retired printers with "Desfazer" (they leave the list, the queue and Quick Quote). */
export function RetiredList() {
  const isAdmin = useIsAdmin();
  const toast = useToast();
  const refresh = useRefreshPrinter();
  const all = useQuery({
    queryKey: ['printers', 'with-retired'],
    queryFn: () => api.get('/printers?limit=200&includeRetired=true', PrinterList),
  });
  const undo = useMutation({
    mutationFn: (id: string) => api.post(`/printers/${id}/unretire`, OkResponse),
    onSuccess: () => {
      refresh();
      toast('Impressora de volta à fila e ao orçamento rápido.');
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const retired = (all.data?.data ?? []).filter((p) => p.retiredAt);
  if (!retired.length) return null;
  return (
    <section className="card col" style={{ gap: 6, marginTop: 18 }}>
      <h2 style={{ fontSize: 18 }}>Aposentadas</h2>
      {retired.map((p) => (
        <div key={p.id} className="row between" style={{ gap: 10 }}>
          <span className="sm">
            {p.name} · aposentada em {formatDate(p.retiredAt!)} com {h1(p.hourMeterHours)} h. Não
            aparece mais na fila nem no orçamento rápido.
          </span>
          {isAdmin ? (
            <button
              type="button"
              className="btn sm"
              disabled={undo.isPending}
              onClick={() => undo.mutate(p.id)}
            >
              Desfazer
            </button>
          ) : null}
        </div>
      ))}
    </section>
  );
}
